import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import PageMeta from '../components/PageMeta';
import { PUBLISHED_PRICE_CENTS, priceLabel, priceAmount, TRIAL_DAYS, INCLUDED, softwareApplicationJsonLd } from '../marketing/pricing';
import './Landing.css';

// Website Sprint 5 (2026-09-13): design pass. Same information architecture,
// same routes, same copy facts; new type (self-hosted Outfit for display),
// one accent (the brand orange ink), a light page with a single navy hero,
// real screenshots in the hero and the feature grid, no emoji icons, no
// fake browser chrome, and restrained motion that respects reduced motion.
//
// Website Sprint 6 (2026-09-14): aligned with v2.8 Team Engagement (RSVP
// chase, team chat, carpool and volunteer sign-ups, live scoreboard, photo
// and video albums, several programs on one login). Those features ship
// behind FEATURE_* flags; the copy below describes them as the app runs
// with the flags on.
//
// Rules this file keeps: react-snap prerender safety (never `text{' '}<a>`,
// build mixed strings with template literals), src/marketing/pricing.test.js
// asserts on visible copy (feature names, "Parent Portal" tab, the parent
// schedule URL caption), and the price comes only from marketing/pricing.js.

// FAQ entries used both for the visible list on the page AND for the FAQPage
// JSON-LD schema below. Keep them in sync: Google penalizes schema that does
// not match visible content.
const FAQS = [
  {
    q: 'How much does HoopHero cost?',
    a: `Coach accounts are ${priceLabel()} after a ${TRIAL_DAYS}-day free trial. One flat fee covers ${INCLUDED}, whether your program charges parents or not. Parent accounts are always free. No setup fees, no per-player fees, cancel anytime.`,
  },
  {
    q: 'Do parents pay HoopHero or pay me directly?',
    a: 'Parents never pay HoopHero. If you run a paid program, parents pay you directly through whatever you choose to accept: Venmo, Cash App, PayPal, Zelle, cash or check, and HoopHero helps you track who paid and when. We never touch the money or take a cut. Free and YMCA programs skip all of this.',
  },
  {
    q: 'What happens after my 14-day free trial?',
    a: `If you subscribe, ${priceLabel()} keeps your account active and everything carries on. If you do not subscribe, your account pauses but your data is not deleted. Come back later and pick up where you left off.`,
  },
  {
    q: 'Is HoopHero for free programs like the YMCA, church leagues, or rec teams?',
    a: 'Yes. When you set up your program you choose Paid, Free, or Free (YMCA). Free programs get rosters, schedule, RSVP, attendance, two-way messaging and reminders with no fee fields, no payment pages and nothing about money shown to parents. YMCA programs pick their branch from our directory so parents recognize the team. The coach subscription is the same low price either way.',
  },
  {
    q: 'Can parents reply to my messages?',
    a: 'Yes. Parents reply from the app or by email (and by text once texting starts), and every reply lands in your coach inbox next to the original message. You can answer one family or the whole team from the same screen.',
  },
  {
    q: 'Do parents get text messages?',
    a: 'Text messages are coming soon, once the carriers approve our texting program. Until then families get every update by email and as a notification on phones with the app installed. When texts start, only parents who tick the "Text me team updates" box get them, and they can reply STOP at any time. You will never pay per text.',
  },
  {
    q: 'How do parents join my program?',
    a: 'You share a program code or join link. Parents create their own free account, add their children and their emergency contact, and they are on your roster. You stop being the data entry person.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. Cancel from your Settings page in two clicks. You keep access through the end of the current billing period, then your card stops being charged. No cancellation fees and no save-the-customer phone calls.',
  },
  {
    q: 'Is my players’ personal info safe? (We have kids under 13)',
    a: 'Yes. HoopHero is built with security best practices: HTTPS everywhere, hashed passwords, JWT sessions, brute-force rate limiting and continuous error monitoring. Youth player data is handled with extra care under COPPA. Parents control their child’s profile, can download everything HoopHero holds about their family, and can delete their account themselves from their profile, no email required. We record which Terms and Privacy version each account accepted, and every time the owner looks at family data it is written to an audit log.',
  },
  {
    q: 'Does HoopHero work on phones?',
    a: 'Yes. HoopHero installs to your home screen on iPhone and Android straight from the browser, no app store, and opens full-screen with its own icon and a bottom tab bar. It keeps showing your schedule, roster and messages when the gym has no signal, and it works in any modern browser on a laptop too.',
  },
  {
    q: 'Do you handle waivers and emergency contacts?',
    a: 'Yes. Every player has an emergency contact that parents keep up to date themselves and coaches see on the attendance sheet. Coaches can publish a participation waiver from a built-in template or paste their own. Parents sign it in the app by typing their name, the roster shows who has and has not signed, and unsigned families get a weekly reminder.',
  },
  {
    q: 'Can parents put the schedule on their phone’s calendar?',
    a: 'Yes. Every coach and parent gets a private calendar link that works with Apple Calendar, Google Calendar and Outlook. Games, practices and events update on their own when the coach changes, cancels or postpones them, and parents can still RSVP per child from the app.',
  },
  {
    q: 'Can I use HoopHero for more than one team or program?',
    a: 'Yes. One coach login runs as many programs as you coach. Add a program from the switcher at the top of your coach sidebar, each with its own roster, schedule, families and settings, and change between them in one click. Your subscription covers every program under your account.',
  },
  {
    q: 'What if parents do not answer the RSVP?',
    a: 'HoopHero chases them for you. Families who have not answered get a reminder 72 hours before each game and again 24 hours before, by email and as a notification on phones with the app installed (and by text for parents who opt in, once texting starts), until they tap Going, Maybe or Not going. Parents can switch the chasers off from their profile and coaches can switch them off for the whole program.',
  },
  {
    q: 'Can grandparents follow the game if they cannot be there?',
    a: 'Yes. You keep score from the schedule during the game, and every game has a scoreboard link you can share. Anyone with the link sees the score and the period update live, no account needed. When you mark the game final the result is saved to the schedule and the top scorers are posted to every family.',
  },
  {
    q: 'Is there a team chat?',
    a: 'Yes. Every program has one team chat for the coach and every family on the roster. You see who has read each message, you can remove a message, and you can mute a parent who is not keeping it friendly. Parents can silence the chat notifications for themselves without leaving it. Turn the chat off for the program from Settings at any time.',
  },
  {
    q: 'Can we share photos and videos of the games?',
    a: 'Yes. Each program has team albums for photos and short videos, visible only to you and the families on your roster. Upload from any phone, and let parents upload too with one switch in Settings. Nothing is public and nothing is posted anywhere else.',
  },
  {
    q: 'Can parents sign up for carpools and snacks?',
    a: 'Yes. Add what each game needs, snacks, a scorekeeper, someone to bring the balls, and parents claim a slot in one tap. Parents with room in the car offer seats and other families ask for a ride. Everyone sees who is covering what right on the schedule.',
  },
];

// FAQPage JSON-LD, eligible for Google’s FAQ rich result. Each entry matches
// a FAQS item above.
const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

/* =====================================================================
   Real screenshots (Website Sprint 4). Captured by CI on the ephemeral
   stack from the demo program (e2e/screenshots.spec.js) and committed to
   client/public/screens/. Desktop above 640px, phone below (CSS).
   ===================================================================== */

const SCREENS = [
  { key: 'coach-dashboard', label: 'Coach Dashboard', path: '/coach/dashboard', alt: 'Coach dashboard: players, upcoming games, record and revenue at a glance, with the getting-started checklist done' },
  { key: 'coach-roster', label: 'Team Roster', path: '/coach/roster', alt: 'Team roster: jersey cards by position with multi-position pills, injured status and a position filter' },
  { key: 'parent-portal', label: 'Parent Portal', path: '/parent/schedule', alt: 'Parent portal: the schedule with one-tap Going / Maybe / Not going RSVP per child, coach messages and payment history' },
];

const Shot = ({ screen, kind, eager }) => (
  <img
    className={`hh-shot hh-shot--${kind}`}
    src={`/screens/${screen.key}-${kind}.png`}
    width={kind === 'desktop' ? 1280 : 390}
    height={kind === 'desktop' ? 800 : 844}
    alt={screen.alt}
    loading={eager ? 'eager' : 'lazy'}
    decoding="async"
  />
);

const FEATURES = [
  { key: 'roster', size: 'third', title: 'Team Roster', desc: 'Jersey-card view by position with medical alerts, multi-position support, and injured or sidelined status.' },
  { key: 'family', size: 'third', title: 'Family Management', desc: 'Parents register with your program code and add their own children. You stop being data entry.' },
  { key: 'schedule', size: 'third', title: 'Schedule, RSVP & Attendance', desc: 'Add games, parents RSVP in one tap, HoopHero chases the no-replies for you, take attendance at tip-off, keep the score.' },
  { key: 'payments', size: 'half', tone: 'navy', title: 'Payment Tracking', desc: 'Paid programs accept Venmo, Cash App, PayPal, Zelle or cash, your call. HoopHero keeps a running balance per family, takes partial payments and one-off charges, and exports a statement. Free programs see nothing about money, anywhere.' },
  { key: 'messaging', size: 'half', tone: 'orange', title: 'Two-Way Messaging', desc: 'Message the whole team or a single family. Parents can reply in the app or by email, and every reply comes back to you. Text messages are coming soon, and you will never pay per text.' },
  { key: 'calendar', size: 'third', title: 'Calendar & Seasons', desc: 'Games, practices and events in a month view with a private calendar feed for Apple, Google and Outlook. Cancel or postpone with one note to every family. Roll over to a new season and keep the archive.' },
  { key: 'waivers', size: 'third', title: 'Waivers & Emergency Contacts', desc: 'Every player carries an emergency contact the coach sees at tip-off. Publish a participation waiver from a built-in template; parents sign by typing their name and the roster shows who has not.' },
  { key: 'reminders', size: 'third', title: 'Automatic Reminders', desc: 'Game-day reminders go out on their own, by email and as a phone notification (texts coming soon). Families who have not RSVPed hear again 72 and 24 hours before tip-off until they answer. Paid programs also get monthly payment reminders.' },
  { key: 'scoreboard', size: 'half', tone: 'navy', title: 'Live Scoreboard', desc: 'Keep score from the schedule with +1, +2, +3 and undo, by period, with points per player. Share one link and grandparents across the country watch the score update live. Mark it final and the result and top scorers go out to every family.' },
  { key: 'chat', size: 'half', tone: 'orange', title: 'Team Chat', desc: 'One chat for the coach and every family. You see who has read each message, you can mute a parent who is not keeping it friendly, and parents can quiet the notifications without leaving the thread.' },
  { key: 'albums', size: 'third', title: 'Photo & Video Albums', desc: 'Team albums for photos and short videos, visible only to your families. You upload from any phone, and parents can too if you let them. Nothing is public.' },
  { key: 'signups', size: 'third', title: 'Carpool & Volunteer Sign-ups', desc: 'Snacks, scorekeeper, who brings the balls: parents claim a slot in one tap. Families with room in the car offer seats and others ask for a ride, right on the schedule.' },
  { key: 'orgs', size: 'third', title: 'Several Programs, One Login', desc: 'Coach two teams or a whole club under one account. Each program keeps its own roster, schedule and families, and you switch between them in one click.' },
  { key: 'install', size: 'full', tone: 'shot', title: 'Installs Like an App', desc: 'Add HoopHero to the home screen on iPhone or Android, no app store. It opens full-screen with a bottom tab bar and keeps showing the schedule, roster and messages when the gym has no signal.' },
];

const BEFORE = [
  { title: 'Missed monthly payments', desc: 'If you charge a fee, you do not remember who paid in March. Some families quietly stop paying. Hundreds of dollars a season are left on the table.' },
  { title: 'The same questions every week', desc: 'What time is the game? Where are we playing? Parents text you because there is no single source of truth.' },
  { title: 'A roster in your other phone', desc: 'First impressions matter to families choosing a program. A messy spreadsheet says you are not serious.' },
  { title: 'Kids miss games', desc: 'One parent never got the game-time text, nobody knows who is coming, and the team plays a player down.' },
  { title: 'Who is driving on Saturday?', desc: 'The carpool and the snack list live in a group text nobody can find, and the grandparents out of state ask you for the score at halftime.' },
];

const AFTER = [
  { title: 'Every payment tracked', desc: 'Paid programs see who has paid this month at a glance and reminders go out on their own. Free programs never see a payment screen.' },
  { title: 'Parents thank you', desc: 'They see the full schedule, RSVP in one tap, get reminders by email and phone notification, and reply to you in one place.' },
  { title: 'You look like a pro', desc: 'Branded portal, real roster, real schedule, real receipts. Parents compare you to club programs that charge far more.' },
  { title: 'More time on the court', desc: 'Less time in email and in three other apps. More time doing what you signed up for.' },
  { title: 'Rides, snacks and scores, sorted', desc: 'Parents claim a seat or a slot from the schedule, and anyone with the scoreboard link follows the game live.' },
];

const PARENT_GETS = [
  'Add your own children, with an emergency contact',
  'See the schedule or subscribe to it from your phone’s calendar',
  'RSVP to games in one tap, per child',
  'Reply to your coach in the app or by email',
  'Sign the team waiver by typing your name',
  'Follow the score live from a link, even from out of town',
  'Chat with the team, and quiet the notifications when you need to',
  'See team photos and videos in one private album',
  'Claim a carpool seat or a volunteer slot from the schedule',
  'Download or delete your data from your profile, any time',
];

// Scroll reveal: adds .is-in to each [data-reveal] element when it enters
// the viewport. Elements start visible (prerendered HTML and no-JS users
// see everything); the .hh-js class on the page root opts in to the hidden
// starting state only once React has mounted, and CSS drops the whole
// effect under prefers-reduced-motion.
export const useReveal = (rootRef) => {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return undefined;
    // react-snap (UA "ReactSnap") serializes the DOM after mount: never hide
    // anything for it, or the prerendered HTML ships with invisible sections.
    if (/ReactSnap/i.test(navigator.userAgent)) return undefined;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;
    root.classList.add('hh-js');
    const els = Array.from(root.querySelectorAll('[data-reveal]'));
    const show = (el) => el.classList.add('is-in');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { show(e.target); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    els.forEach((el) => io.observe(el));
    // Fail open: whatever the observer has not reached shows itself anyway.
    const failOpen = setTimeout(() => { els.forEach(show); io.disconnect(); }, 1200);
    return () => { clearTimeout(failOpen); io.disconnect(); };
  }, [rootRef]);
};

const Landing = () => {
  const navigate = useNavigate();
  const [activeScreen, setActiveScreen] = useState(0);
  const rootRef = useRef(null);
  useReveal(rootRef);
  const screens = SCREENS;
  const current = screens[activeScreen];

  return (
    <div className="hh-page" ref={rootRef}>
      <PageMeta
        title="HoopHero — Run Your Youth Basketball Program Like a Pro"
        description="The youth basketball coach app for paid, free, and YMCA programs. Rosters, schedules, RSVP that chases the no-replies, live scoreboard link, team chat, photo and video albums, carpool and volunteer sign-ups, two-way messaging, plus payment tracking for paid programs. 14-day free trial, cancel anytime."
        path="/"
      />
      <Helmet>
        {/* Machine-readable published price; e2e/website-price-guard.spec.js
            compares it with /api/config so marketing copy and Stripe agree. */}
        <meta name="hoophero:published-price-cents" content={String(PUBLISHED_PRICE_CENTS)} />
        <script type="application/ld+json">{JSON.stringify(softwareApplicationJsonLd())}</script>
      </Helmet>
      {/* FAQ JSON-LD only on this page, eligible for Google FAQ rich results. */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(FAQ_SCHEMA)}
        </script>
      </Helmet>

      <nav className="hh-nav" aria-label="Site">
        <a className="hh-nav-logo" href="/" aria-label="HoopHero home">
          <img
            src="/logo-wide.png"
            alt="HoopHero — youth basketball program management software"
            className="hh-nav-logo-img"
            width="1119"
            height="390"
          />
        </a>
        <div className="hh-nav-links">
          <button className="hh-nav-link hh-nav-link--coach" onClick={() => navigate('/coach/login')}>Coach login</button>
          <button className="hh-nav-link hh-nav-link--parent" onClick={() => navigate('/parent/login')}>Parent login</button>
          <button className="hh-nav-cta" onClick={() => navigate('/coach/register')}>Start free trial</button>
        </div>
      </nav>

      <main>
      {/* ============================================================
          HERO. Split: the promise on the left, the real coach dashboard
          on a phone on the right. H1 is the primary keyword target.
          ============================================================ */}
      <header className="hh-hero">
        <div className="hh-hero-inner">
          <div className="hh-hero-content">
            <h1 className="hh-hero-title">
              {'Run your youth basketball program '}
              <span className="hh-hero-accent">like a pro.</span>
            </h1>
            <p className="hh-hero-sub">
              Rosters, schedule, RSVP, team chat, live scores and two-way messaging, plus payment tracking for paid programs, built only for youth basketball coaches. Paid, free and YMCA programs.
            </p>
            <div className="hh-hero-btns">
              <button className="hh-btn hh-btn--primary hh-btn--lg" onClick={() => navigate('/coach/register')}>
                Start free trial
              </button>
              <button className="hh-btn hh-btn--ghost-light hh-btn--lg" onClick={() => navigate('/parent/register')}>
                Parents: join your team
              </button>
            </div>
          </div>
          <div className="hh-hero-visual" aria-hidden="false">
            <div className="hh-device">
              <img
                className="hh-device-shot"
                src="/screens/coach-dashboard-phone.png"
                width={390}
                height={844}
                alt="Coach dashboard on a phone: players, upcoming games, record and revenue at a glance"
                loading="eager"
                decoding="async"
                fetchpriority="high"
              />
            </div>
          </div>
        </div>
      </header>

      {/* ============================================================
          EMPATHY. Name the pain the coach is living so they know we get it.
          ============================================================ */}
      <section className="hh-empathy">
        <div className="hh-empathy-inner" data-reveal>
          <h2 className="hh-display">Coaching should not feel like a second full-time job.</h2>
          <p className="hh-lead">
            You signed up to teach kids basketball. Not to chase down monthly checks, dig through three group texts for the game time, or apologize because the roster is in your other phone.
          </p>
          <p className="hh-lead">
            Youth coaches deserve professional tools. HoopHero is built for one thing, running a youth basketball program, so you can stop juggling apps and get back on the court.
          </p>
        </div>
      </section>

      {/* ============================================================
          FREE PROGRAMS: YMCA, church, rec, volunteer (Website Epic WD2)
          ============================================================ */}
      <section className="hh-free-programs">
        <div className="hh-free-inner" data-reveal>
          <div className="hh-free-copy">
            <h2 className="hh-h2">Run a free program? HoopHero fits.</h2>
            <p className="hh-body">
              YMCA leagues, church teams, rec programs, volunteer coaches: pick <strong>Free</strong> or <strong>Free (YMCA)</strong> when you sign up and the app drops everything about money. No fee fields, no payment pages, nothing about a price in front of your parents. You keep the roster, schedule, RSVP, attendance, two-way messaging and automatic reminders.
            </p>
            <p className="hh-free-link-row">
              <a className="hh-link-btn hh-free-link" href="/ymca-basketball">Coaching at a YMCA? See the page built for you →</a>
            </p>
          </div>
          <dl className="hh-free-strip">
            <div className="hh-free-item">
              <dt>YMCA directory</dt>
              <dd>Pick your state, then your branch. Your YMCA is shown to you and your parents so the team is easy to recognize.</dd>
            </div>
            <div className="hh-free-item">
              <dt>No payment surfaces</dt>
              <dd>Free programs never see a Payments page, a fee, or a paid or unpaid badge, coach side or parent side.</dd>
            </div>
            <div className="hh-free-item">
              <dt>Change your mind later</dt>
              <dd>Switch a program between Paid and Free from Settings at any time. Nothing is deleted.</dd>
            </div>
          </dl>
        </div>
      </section>

      {/* ============================================================
          THE PLAN. Three steps, one CTA.
          ============================================================ */}
      <section className="hh-plan">
        <div className="hh-plan-inner" data-reveal>
          <h2 className="hh-h2 hh-h2--center">Run your season in three steps.</h2>
          <ol className="hh-steps">
            <li className="hh-step">
              <span className="hh-step-num" aria-hidden="true">1</span>
              <h3 className="hh-step-title">Start your free trial</h3>
              <p className="hh-step-desc">14 days free, no card required. Sign up in under two minutes.</p>
            </li>
            <li className="hh-step">
              <span className="hh-step-num" aria-hidden="true">2</span>
              <h3 className="hh-step-title">Add your roster</h3>
              <p className="hh-step-desc">Invite parents with a program code. They register their kids, you do not.</p>
            </li>
            <li className="hh-step">
              <span className="hh-step-num" aria-hidden="true">3</span>
              <h3 className="hh-step-title">Coach your season</h3>
              <p className="hh-step-desc">Schedule, RSVP, chat, live scores, sign-ups and reminders, handled. Payment tracking too, if you run a paid program. Spend your time on the court.</p>
            </li>
          </ol>
          <div className="hh-plan-cta">
            <button className="hh-btn hh-btn--primary" onClick={() => navigate('/coach/register')}>
              Start free trial
            </button>
          </div>
        </div>
      </section>

      {/* ============================================================
          FEATURES. Nine cells, mixed sizes; two tinted, one with the
          parent app on a phone.
          ============================================================ */}
      <section className="hh-features">
        <div className="hh-features-inner">
          <div className="hh-features-head" data-reveal>
            <h2 className="hh-h2">Built for one job: running a youth basketball program.</h2>
            <p className="hh-body">Not generic team-sports software. Every feature designed for youth basketball coaches running one team or a whole club.</p>
          </div>
          <ul className="hh-bento">
            {FEATURES.map((f, i) => (
              <li key={f.key} className={`hh-cell hh-cell--${f.size}${f.tone ? ` hh-cell--${f.tone}` : ''}`} data-reveal style={{ '--i': i }}>
                <div className="hh-cell-text">
                  <h3 className="hh-cell-title">{f.title}</h3>
                  <p className="hh-cell-desc">{f.desc}</p>
                </div>
                {f.tone === 'shot' && (
                  <div className="hh-cell-shot">
                    <img
                      src="/screens/parent-portal-phone.png"
                      width={390}
                      height={844}
                      alt="HoopHero installed on a phone: the parent schedule with one-tap RSVP"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============================================================
          BEFORE / AFTER. Stakes and transformation side by side.
          ============================================================ */}
      <section className="hh-compare">
        <div className="hh-compare-inner" data-reveal>
          <h2 className="hh-h2 hh-h2--center">What changes when the program is organized.</h2>
          <div className="hh-compare-grid">
            <div className="hh-compare-col hh-compare-col--before">
              <h3 className="hh-compare-head">Without HoopHero</h3>
              <ul>
                {BEFORE.map((b) => (
                  <li key={b.title} className="hh-compare-item">
                    <h4 className="hh-compare-title">{b.title}</h4>
                    <p className="hh-compare-desc">{b.desc}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div className="hh-compare-col hh-compare-col--after">
              <h3 className="hh-compare-head">With HoopHero</h3>
              <ul>
                {AFTER.map((a) => (
                  <li key={a.title} className="hh-compare-item">
                    <h4 className="hh-compare-title">{a.title}</h4>
                    <p className="hh-compare-desc">{a.desc}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          SCREENSHOTS. Show the product.
          ============================================================ */}
      <section className="hh-screenshots">
        <div className="hh-screenshots-inner" data-reveal>
          <h2 className="hh-h2 hh-h2--center">See HoopHero in action.</h2>
          <div className="hh-screenshots-tabs" aria-label="Screens">
            {screens.map((s, i) => (
              <button
                key={s.label}
                aria-pressed={activeScreen === i}
                onClick={() => setActiveScreen(i)}
                className={`hh-screenshot-tab${activeScreen === i ? ' hh-screenshot-tab--active' : ''}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <figure className="hh-screenshot-frame">
            <div className="hh-mock-desktop">
              <Shot screen={current} kind="desktop" eager={activeScreen === 0} />
            </div>
            <div className="hh-mock-mobile">
              <Shot screen={current} kind="phone" eager={activeScreen === 0} />
            </div>
            <figcaption className="hh-screenshot-caption">{`hoophero.net${current.path}`}</figcaption>
          </figure>
        </div>
      </section>

      {/* ============================================================
          PRICING
          ============================================================ */}
      <section className="hh-pricing">
        <div className="hh-pricing-inner" data-reveal>
          <h2 className="hh-h2 hh-h2--center">Simple, honest pricing.</h2>
          <div className="hh-pricing-grid">
            <div className="hh-plan-card hh-plan-card--coach">
              <div className="hh-plan-label">Coach account</div>
              <div className="hh-plan-price">{priceAmount()}<span className="hh-plan-per">/month</span></div>
              <p className="hh-plan-desc">{`One flat fee per coach, for paid, free or YMCA programs. Covers ${INCLUDED}.`}</p>
              <ul className="hh-plan-list">
                {[
                  '14-day free trial, no card needed',
                  'Unlimited basketball programs on one login',
                  'Unlimited seasons',
                  'Unlimited families, players and games',
                  'Full coach dashboard',
                  'Schedule and game management',
                  'Payment tracking (paid programs)',
                  'Two-way messaging with families',
                  'Automatic reminders (texts coming soon)',
                  'RSVP and attendance',
                  'Live scoreboard with a shareable link',
                  'Team chat, photo and video albums',
                  'Carpool and volunteer sign-ups',
                  'Cancel anytime',
                ].map((f) => (
                  <li key={f} className="hh-plan-item">{f}</li>
                ))}
              </ul>
              <button className="hh-btn hh-btn--primary hh-btn--full" onClick={() => navigate('/coach/register')}>
                Start free trial
              </button>
              <div className="hh-plan-foot">No credit card required for the trial</div>
            </div>

            <div className="hh-plan-card hh-plan-card--parent">
              <div className="hh-plan-label">Parent account</div>
              <div className="hh-plan-price">{'Free '}<span className="hh-plan-per">forever</span></div>
              <p className="hh-plan-desc">Parent accounts are always free. No subscriptions, no hidden fees.</p>
              <ul className="hh-plan-list">
                {[
                  'Add your own children',
                  'View the full game schedule',
                  'RSVP to games in one tap',
                  'Reply to your coach in the app',
                  'Email, phone notifications and reminders',
                  'Medical info for each child',
                  'Live scores, team chat and albums',
                  'Carpool and volunteer sign-ups',
                  'Payment history (paid programs)',
                ].map((f) => (
                  <li key={f} className="hh-plan-item">{f}</li>
                ))}
              </ul>
              <button className="hh-btn hh-btn--navy hh-btn--full" onClick={() => navigate('/parent/register')}>
                Join your team
              </button>
              <div className="hh-plan-foot">Join with the program code from your coach</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          PARENTS. Secondary audience.
          ============================================================ */}
      <section className="hh-parents">
        <div className="hh-parents-inner" data-reveal>
          <div className="hh-parents-copy">
            <h2 className="hh-h2">Are you a parent?</h2>
            <p className="hh-body">
              Your coach will share a program code or join link. Use it to create your free parent account. Paid programs also show your balance and payment history; free programs never mention money. Your data is yours: download it or delete your account from your profile any time.
            </p>
            <div className="hh-parents-actions">
              <button className="hh-btn hh-btn--navy" onClick={() => navigate('/parent/register')}>
                Join your team
              </button>
              <button className="hh-link-btn" onClick={() => navigate('/parent/login')}>
                Already have an account? Sign in
              </button>
            </div>
          </div>
          <ul className="hh-parents-list" aria-label="What parents get">
            {PARENT_GETS.map((p) => (
              <li key={p} className="hh-parents-item">{p}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============================================================
          FAQ. Common questions plus the JSON-LD FAQPage schema in <head>.
          ============================================================ */}
      <section className="hh-faq">
        <div className="hh-faq-inner" data-reveal>
          <h2 className="hh-h2 hh-h2--center">Common questions</h2>
          <div className="hh-faq-list">
            {FAQS.map((f) => (
              <details key={f.q} className="hh-faq-item">
                <summary className="hh-faq-q">{f.q}</summary>
                <p className="hh-faq-a">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          FINAL CTA
          ============================================================ */}
      <section className="hh-final">
        <div className="hh-final-inner" data-reveal>
          <h2 className="hh-display hh-display--center">Ready to coach like a pro?</h2>
          <p className="hh-lead hh-lead--center">
            {`Start your ${TRIAL_DAYS}-day free trial in under two minutes. No credit card. ${priceLabel()} after, cancel anytime.`}
          </p>
          <button className="hh-btn hh-btn--primary hh-btn--lg" onClick={() => navigate('/coach/register')}>
            Start free trial
          </button>
        </div>
      </section>
      </main>

      <footer className="hh-footer">
        <div className="hh-footer-inner">
          <img
            src="/logo-wide.png"
            alt="HoopHero — youth basketball program management software"
            className="hh-footer-logo-img"
            width="1119"
            height="390"
            loading="lazy"
          />
          <div className="hh-footer-links">
            <button className="hh-footer-link" onClick={() => navigate('/coach/login')}>Coach login</button>
            <button className="hh-footer-link" onClick={() => navigate('/parent/login')}>Parent login</button>
            <button className="hh-footer-link" onClick={() => navigate('/coach/register')}>Start free trial</button>
            <button className="hh-footer-link" onClick={() => navigate('/getting-started')}>Getting started</button>
            <button className="hh-footer-link" onClick={() => navigate('/terms')}>Terms</button>
            <button className="hh-footer-link" onClick={() => navigate('/privacy')}>Privacy</button>
            <button className="hh-footer-link" onClick={() => navigate('/sms')}>Text messages</button>
          </div>
          <div className="hh-footer-note">© 2026 Clarity Growth Pro LLC. HoopHero is built for youth basketball coaches.</div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
